import {
    Badge,
    BadgeText,
    ButtonIconTooltip,
    HamburgerIcon,
    HStack,
    Image,
    Text,
    useMobileView
} from "@hosttools/frontend";
import React, {memo, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {Link, matchPath, Redirect, useLocation} from "react-router-dom";

import {INBOX_PATH, INBOX_URL, MESSAGING_PATH, MULTICALENDAR_PATH, PRICING_PATH} from "../constant";
import logoBlack from "../img/logo-black.svg";
import {UserContext} from "../providers/UserProvider";
import {BrowserNotification} from "../utils/browserNotification";

import Banner from "./Banner";
import ModalAddAccount from "./ModalAddAccount";
import NavMessaging from "./NavMessaging";
import NavPricing from "./NavPricing";

import MenuProfile from "@/client/components/Header/MenuProfile";
import ModalMenuMobile from "@/client/components/Header/ModalMenuMobile";
import TopNavMenuItem from "@/client/components/Header/TopNavMenuItem";
import ReferralButton from "@/client/components/Settings/Referral/ReferralButton";
import QuickGuideButton from "@/client/features/QuickGuide/QuickGuideButton";
import SupportMenu from "@/client/features/SupportMenu";

const logoSource = {uri: logoBlack};

const Header: React.FC = () => {
    const {
        user: {subscriptionStatus},
        visibleListings,
        listingGroups,
        permissions
    } = useContext(UserContext);
    const location = useLocation();
    const isMobile = useMobileView();
    const {pathname} = location;

    const [showAddAccountModal, setShowAddAccountModal] = useState(false);
    const [showModalMenuMobile, setShowModalMenuMobile] = useState(false);
    const [showBannerAskNotification, setShowBannerAskNotification] = React.useState(false);
    const browserNotifyRef = useRef<BrowserNotification | null>(null);
    const browserNotification = browserNotifyRef.current;
    const hasPermissionEditInbox = permissions.inbox?.edit;
    const hasPermissionEditPricing = permissions.pricing?.edit;
    const hasPermissionEditMessageRules = permissions.messageRules?.edit;

    useEffect(() => {
        browserNotifyRef.current = new BrowserNotification();
    }, []);

    const handleCloseBanner = () => {
        setShowBannerAskNotification(false);
    };

    const bannerNotificationRenderer = useMemo(() => {
        if (showBannerAskNotification) {
            const handleAskPermission = () => {
                browserNotification?.requestPermission(() => {
                    setShowBannerAskNotification(false);
                });
            };

            return (
                browserNotification?.shouldAskPermission() && (
                    <Banner
                        action={handleAskPermission}
                        actionText="Enable/Disable"
                        actionType="warning"
                        onClose={handleCloseBanner}
                    >
                        <Text color="blueGray.800" variant="md">
                            Browser Notifications.{" "}
                        </Text>
                        <Text variant="sm">
                            Would you like message and reservation notifications in your browser?
                        </Text>
                    </Banner>
                )
            );
        }

        return null;
    }, [showBannerAskNotification, browserNotification]);

    function handleCloseModal(isSuccess?: boolean) {
        setShowAddAccountModal(false);
        if (typeof isSuccess === "boolean" && isSuccess) {
            setShowBannerAskNotification(true);
        }
    }

    function handleContactMenu() {
        const intercom = window.Intercom;
        if (typeof intercom === "function") {
            intercom("show");
        }
    }

    const handleToggleMenuMobile = useCallback(() => {
        setShowModalMenuMobile(prev => !prev);
    }, []);

    const regexCalendar = /#!\/calendar\/(.*)/;
    const foundCalendar = location.hash.match(regexCalendar);
    if (foundCalendar) {
        return <Redirect to={`/pricing/${foundCalendar[1]}`} />;
    }
    const regexMessageRules = /#!\/messageRules\/(.*)/;
    const foundMessageRules = location.hash.match(regexMessageRules);
    if (foundMessageRules) {
        return <Redirect to={`/messaging/${foundMessageRules[1]}`} />;
    }
    const regexListings = /#!\/listings/;
    const foundListings = location.hash.match(regexListings);
    if (foundListings) {
        return <Redirect to="/listings" />;
    }
    const regexBilling = /#!\/billing/;
    const foundBilling = location.hash.match(regexBilling);
    if (foundBilling) {
        return <Redirect to="/billing" />;
    }
    const regexSettings = /#!\/settings/;
    const foundSettings = location.hash.match(regexSettings);
    if (foundSettings) {
        return <Redirect to="/settings" />;
    }

    const isCalendarActive = !!matchPath(pathname, {path: MULTICALENDAR_PATH, exact: true});
    const isInboxActive = !!matchPath(pathname, {path: INBOX_PATH, exact: true});
    const isMessagingActive = !!matchPath(pathname, {
        path: MESSAGING_PATH,
        exact: true
    });
    const isPricingActive = !!matchPath(pathname, {path: PRICING_PATH, exact: true});

    let subscriptionBadge: React.ReactNode;
    if (__IS_BETA__) {
        subscriptionBadge = (
            <Badge action="warning" variant="solid">
                <BadgeText>Beta</BadgeText>
            </Badge>
        );
    } else if (subscriptionStatus === "trialing") {
        subscriptionBadge = (
            <Badge action="warning" variant="solid">
                <BadgeText>Trial</BadgeText>
            </Badge>
        );
    }

    return (
        <>
            <HStack
                className="min-h-[60px] w-full sticky top-0 z-20 px-4 md:px-7 bg-white justify-between items-cente border-b border-blueGray-200 border-solid"
                testID="top-header"
            >
                <HStack className="h-full items-center" space="lg">
                    <HStack className="items-center" space="sm">
                        {isMobile && (
                            <ButtonIconTooltip
                                icon={HamburgerIcon}
                                size="lg"
                                tooltip="Menu"
                                onPress={handleToggleMenuMobile}
                            />
                        )}
                        <Link to="/">
                            <Image
                                alt="Host Tools Logo"
                                className="h-[30px] w-[150px]"
                                source={logoSource}
                            />
                        </Link>
                    </HStack>
                    {!isMobile && (
                        <HStack className="h-full" testID="nav-menu-wrapper">
                            <TopNavMenuItem isActive={isCalendarActive} to={MULTICALENDAR_PATH}>
                                Calendar
                            </TopNavMenuItem>
                            {hasPermissionEditInbox && (
                                <TopNavMenuItem isActive={isInboxActive} to={INBOX_URL}>
                                    Inbox
                                </TopNavMenuItem>
                            )}
                            {hasPermissionEditMessageRules && (
                                <NavMessaging
                                    isActive={!!isMessagingActive}
                                    listingGroups={listingGroups}
                                    visibleListings={visibleListings}
                                />
                            )}
                            {hasPermissionEditPricing && (
                                <NavPricing
                                    isActive={!!isPricingActive}
                                    visibleListings={visibleListings}
                                />
                            )}
                        </HStack>
                    )}
                </HStack>
                <HStack className="justify-center items-center" space="sm">
                    <ReferralButton />
                    {!isMobile &&
                        subscriptionStatus !== "active" &&
                        subscriptionStatus !== "canceled" && <QuickGuideButton />}
                    {subscriptionBadge}
                    <SupportMenu onSupport={handleContactMenu} />
                    <MenuProfile />
                </HStack>
            </HStack>
            {showAddAccountModal && <ModalAddAccount onHide={handleCloseModal} />}
            {showModalMenuMobile && (
                <ModalMenuMobile
                    isOpen={showModalMenuMobile}
                    subscriptionStatus={subscriptionStatus || ""}
                    onHide={handleToggleMenuMobile}
                />
            )}
            {bannerNotificationRenderer}
        </>
    );
};

export default memo(Header);
